<template>
    <div v-if="isScorecardUnavailable" class="scorecard-empty-state-container">
        <div class="empty-state" style="max-width: 13.75rem">
            <img src="@/assets/img/scorecard-empty-state.png" style="width: 13.75rem" />
            <Spacer height="2.5rem" />
            <Text as="p" size="f-8" align="center">
                This Scorecard has been deleted. Please click below to view existing Scorecards or
                create a new one.
            </Text>
            <Spacer height="3rem" />
            <oButton size="large" color="blue" @clicked="goToScorecards"> View Scorecards </oButton>
        </div>
    </div>
    <div v-else-if="scorecard && scorecard.account">
        <OnboardingModal
            v-model="renderOnboardingStep"
            @closed="showOnboarding = false"
            position="fixed"
            button-text="Dismiss Notification"
            transition="fade-left"
            :set-bounding-rect="{ top: '1.25rem', left: '1.25rem' }"
            style="padding: 2rem 2.25rem"
            :column="true"
            max-width="30.25rem"
        >
            <template #copy>
                Opteo can only find Performance Max or Shopping campaigns in this account. This will
                result in several Scorecard sections without relevant and score-able data. Please
                submit a feature request if you would like to see more Performance Max features in
                Opteo.
            </template>
        </OnboardingModal>

        <ScorecardContent
            v-if="scorecardAccountInfo"
            :scorecard-body="scorecard.body"
            :scorecard-history="scorecardHistory"
            :account-info="scorecardAccountInfo"
            :created-at="scorecard.createdAt"
            :overview-score="scorecard.overviewScore"
            :link-share-loading="linkShareLoading"
            :download-loading="isDownloading"
            :download-failed="isDownloadFailed"
            mode="app"
            @copy-share-link="copyShareLink"
            @clicked-account-name="accountEditModalOpen = true"
            @clicked-sender-name="senderEditModalOpen = true"
            @clicked-edit-cover="showEditCoverModal = true"
            @clicked-download="download"
            @clicked-delete="$emit('clicked-delete')"
            :is-auto-generated="autoGenerated"
            :breakdowns="scorecardBreakdowns"
            :loading-breakdowns="scorecardLoadingBreakdowns"
            @breakdown-requested="sectionType => fetchBreakdown(sectionType)"
        />
    </div>

    <!-- Account Name Edit Modal. -->
    <Modal v-model="accountEditModalOpen" title="Cover Settings" width="64.25rem" no-padding>
        <template #content>
            <div class="cover-modal-content-container" @keyup.enter="updateAccountInfo">
                <div class="section">
                    <Spacer height="2.5rem" />
                    <AccountInfoForm
                        :register-submit-function="registerAccountInfoSubmitFunction"
                        :account-logo-url="coverPageData?.accountLogoUrl ?? ''"
                        :on-account-logo-url-update="updateAccountLogoUrl"
                        :image-upload-disabled="coverPageDataLoading"
                        :account-name="coverPageData?.accountName ?? ''"
                        :website="coverPageData?.website ?? null"
                        :industry="coverPageData?.industry ?? null"
                        :location="coverPageData?.location ?? null"
                        autofocusInput
                    />
                </div>
            </div>
        </template>
        <template #buttons>
            <oButton color="white" @clicked="accountEditModalOpen = false">Cancel</oButton>
            <Spacer width="0.625rem" />
            <oButton color="blue" @clicked="updateAccountInfo">Update Cover Information</oButton>
        </template>
    </Modal>

    <!-- Sender Name Edit Modal -->
    <Modal v-model="senderEditModalOpen" title="Footer Settings" width="64.25rem" no-padding>
        <template #content>
            <div class="cover-modal-content-container" @keyup.enter="updateSenderInfo">
                <div class="section">
                    <Spacer height="2.5rem" />
                    <SenderInfoForm
                        v-if="coverPageData"
                        :register-submit-function="registerSenderInfoFormSubmitFunction"
                        :sender-user-id="userId ?? undefined"
                        :sender-email="coverPageData.senderEmail"
                        :sender-name="coverPageData.senderName"
                        :sender-logo-url="coverPageData.senderLogoUrl"
                        :on-sender-logo-url-update="updateSenderLogoUrl"
                        :image-upload-disabled="coverPageDataLoading"
                        autofocusInput
                        :is-auto-generated="autoGenerated"
                    />
                </div>
            </div>
        </template>
        <template #buttons>
            <oButton color="white" @clicked="senderEditModalOpen = false">Cancel</oButton>
            <Spacer width="0.625rem" />
            <oButton color="blue" @clicked="updateSenderInfo">Update Footer Settings</oButton>
        </template>
    </Modal>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onUpdated, onBeforeUnmount, watch } from 'vue'

import { delay } from '@opteo/promise'

import { API_URL } from '@/lib/env'

import { OnboardingModal, Spacer, oButton, Modal, showSnackbar, Text } from '@opteo/components-next'

import ScorecardContent from '@/components/scorecard/ScorecardContent.vue'
import AccountInfoForm from '@/components/scorecard/settings/AccountInfoForm.vue'
import SenderInfoForm from '@/components/scorecard/settings/SenderInfoForm.vue'

import { useScorecardSingle } from '@/composition/toolkit/scorecard/useScorecardSingle'
import { useScorecardBreakdowns } from '@/composition/toolkit/scorecard/useScorecardBreakdowns'
import { useScorecardCoverEditModal } from '@/composition/toolkit/scorecard/useScorecardCoverEditModal'
import { useDownloadFile } from '@/composition/api/useDownloadFile'

import { Scorecard } from '@opteo/types'
import { Platform } from '@opteo/types'

import { useRouter } from 'vue-router'
import { Routes } from '@/router/routes'

export default defineComponent({
    name: 'ToolkitScorecard',
    components: {
        Spacer,
        oButton,
        Modal,
        ScorecardContent,
        AccountInfoForm,
        SenderInfoForm,
        Text,
        OnboardingModal,
    },
    emits: ['is-single-scorecard', 'clicked-delete', 'is-closing-scorecard'],
    setup(_, { emit }) {
        const scorecardSingleInfo = useScorecardSingle()
        const {
            coverPageData,
            coverPageDataLoading,

            registerAccountInfoSubmitFunction,
            registerSenderInfoFormSubmitFunction,

            showEditCoverModal,
            accountEditModalOpen,
            senderEditModalOpen,

            updateAccountInfo,
            updateAccountLogoUrl,
            updateSenderInfo,
            updateSenderLogoUrl,
        } = useScorecardCoverEditModal()

        const { scorecardId, scorecard, isScorecardUnavailable } = scorecardSingleInfo
        const { scorecardBreakdowns, scorecardLoadingBreakdowns, fetchBreakdown } =
            useScorecardBreakdowns({ scorecardId })

        // userId is of weird RemovableRef type and was acting weirdly on the template, so doing this instead
        const userId = scorecardSingleInfo.userId.value

        const router = useRouter()
        const goToScorecards = () => router.push({ name: Routes.ToolkitScorecardList })

        onUpdated(async () => {
            emit('is-single-scorecard', {
                scorecardId,
                isAutoGenerated: scorecard?.value?.autoGenerated,
                isScorecardUnavailable: isScorecardUnavailable?.value,
            })
        })

        onBeforeUnmount(() => {
            emit('is-closing-scorecard')
        })

        const autoGenerated = computed(
            () => scorecardSingleInfo?.scorecard?.value?.autoGenerated ?? false
        )

        const tableRowHovered = ref(null)

        const exportScorecardUrl = computed(
            () => `${API_URL}/render/render_scorecard/?sc=${scorecardId.value}&user_id=${userId}`
        )

        const filename = computed(() => `${coverPageData?.value?.accountName} Scorecard.pdf`)

        const {
            isDownloading,
            hasErrored: isDownloadFailed,
            download,
            resetErrorState,
        } = useDownloadFile(exportScorecardUrl, filename, 'application/pdf')

        watch(isDownloadFailed, async newVal => {
            if (newVal) {
                showSnackbar({
                    message: 'Something went wrong. Please try again later.',
                })
                await delay(2000)
                resetErrorState()
            }
        })

        return {
            SectionTypes: Scorecard.SectionTypes,
            ...scorecardSingleInfo,
            scorecardId,
            scorecard,
            isScorecardUnavailable,
            userId,
            Platform,
            tableRowHovered,
            download,
            isDownloading,
            isDownloadFailed,
            goToScorecards,
            coverPageData,
            coverPageDataLoading,
            registerAccountInfoSubmitFunction,
            registerSenderInfoFormSubmitFunction,
            showEditCoverModal,
            accountEditModalOpen,
            senderEditModalOpen,
            updateAccountInfo,
            updateAccountLogoUrl,
            updateSenderInfo,
            updateSenderLogoUrl,
            autoGenerated,
            fetchBreakdown,
            scorecardBreakdowns,
            scorecardLoadingBreakdowns,
        }
    },
})
</script>

<style lang="scss" scoped>
@import '@/assets/css/theme.scss';
@import '@/assets/css/variables.scss';

a {
    color: $opteo-dark-blue;
    text-decoration: underline;
    text-underline-offset: 0.125rem;
}

// Empty states
.scorecard-empty-state-container {
    @include w-100;
    @include container;
    border-radius: 2.75rem;
    @include pa-40;
    height: 34.375rem;
    @include flex-center;
}
.empty-state {
    @include flex;
    flex-direction: column;
    @include items-center;
}

.cover-modal-content-container .section {
    @include pt-48;
    @include pb-52;
    max-width: 34.5rem;
    margin: 0 auto;
}

// Responsive
// @media (max-width: $mq-480-max) {
// }

// @media screen and (min-width: $mq-481-min) and (max-width: $mq-767-max) {
// }
</style>
